import { PressableScale } from "pressto";
import { View, Text, Card } from "@src/components";
import { UnistylesRuntime } from "react-native-unistyles";
import type { FC, ReactNode } from "react";
import type { AlarmRoutine } from "@src/services/api/types";
import { formatRoutineForDisplay } from "@src/features/alarms/utils/format-routine";
import { Icon, IconNameType } from "@src/components/Icon";
import { AlarmStateBadge } from "./AlarmStateBadge";
import { memo } from "react";

interface RoutinePreviewProps {
  routine: AlarmRoutine | null;
  onPress?: () => void;
  accessoryIcon?: IconNameType;
  accessory?: ReactNode;
  disabled?: boolean;
}

export const RoutinePreview: FC<RoutinePreviewProps> = memo(
  ({ routine, onPress, accessoryIcon, accessory, disabled = false }) => {
    const theme = UnistylesRuntime.getTheme();
    const formattedWindows = routine ? formatRoutineForDisplay(routine) : [];
    const accessoryNode =
      accessory ?? (onPress && accessoryIcon ? <Icon name={accessoryIcon} color={theme.colors.icon} /> : null);

    const content = (
      <Card
        borderWidth={1}
        style={{ opacity: disabled ? 0.5 : 1, borderColor: theme.colors.alarmStates.routinePreviewBorder }}
      >
        <View flexDirection="row" justifyContent="space-between">
          <View flex={1}>
            <View flexDirection="row" alignItems="center" gap="md" mb="2xl">
              <View
                w={20}
                h={20}
                rounded={4}
                style={{
                  backgroundColor: theme.colors.alarmStates.armed as string,
                }}
              />
              <Text textStyle="sm/medium">Routine</Text>
              <AlarmStateBadge state={"armed"} />
            </View>
            <View gap="sm">
              {formattedWindows.length > 0 &&
                formattedWindows.map((window, index) => (
                  <View key={index} flexDirection="row" alignItems="center" gap="xs">
                    <View flexDirection="row" alignItems="center" gap="xs">
                      <Icon name="calendarOutline" color={theme.colors.textMuted} size={16} />
                      <Text textStyle="xs/regular" color="textMuted">
                        {window.name}
                      </Text>
                    </View>
                    {window.timeRanges ?
                      <>
                        <Text textStyle="xs/regular" color="textMuted">
                          •
                        </Text>
                        <View flexDirection="row" alignItems="center" gap="xs">
                          <Icon name="clockOutline" color={theme.colors.textMuted} size={16} />
                          <Text
                            textStyle="xs/regular"
                            color="textMuted"
                            numberOfLines={1}
                            ellipsizeMode="middle"
                            style={{ flexShrink: 1 }}
                          >
                            {window.timeRanges}
                          </Text>
                        </View>
                      </>
                    : null}
                  </View>
                ))}
              {!routine && (
                <Text textStyle="sm/regular" color="textMuted">
                  No routine configured
                </Text>
              )}
            </View>
          </View>
          {accessoryNode && <View ml="3xl">{accessoryNode}</View>}
        </View>
      </Card>
    );

    if (!onPress) {
      return <View>{content}</View>;
    }

    return (
      <PressableScale onPress={disabled ? undefined : onPress} enabled={!disabled}>
        {content}
      </PressableScale>
    );
  },
);
